/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */
/**
 * ---------------------------------------------------------------------------------------------------------------------
 * A/B TEST: ANNOUNCEMENT BAR ROTATOR FOR THE UPDATED DESIGN
 * ---------------------------------------------------------------------------------------------------------------------
 *
 * The updated announcement bar carries its own copy, so both buckets' messages sit inside the one <announcement-bar>
 * element (which owns --announcement-bar-height and must not be duplicated). The theme's own carousel collects every
 * <announcement-bar-item> on the page and would keep rotating through the hidden bucket, blanking the visible bar, so
 * the updated messages are plain elements cycled here instead.
 *
 * The transition intentionally mirrors the theme's own announcement-bar-item select/deselect animation, so switching
 * buckets does not change how the bar feels.
 */
(function () {
  var FADE_DURATION = 150;
  var FADE_EASING = 'ease-in-out';

  var AnnouncementRotator = class extends HTMLElement {
    connectedCallback() {
      this.messages = Array.from(this.children);

      // A single message never moves, and the theme editor has nothing to switch between.
      if (this.messages.length < 2) {
        return;
      }

      this.selectedIndex = 0;
      this.hasPendingTransition = false;

      if (window.Shopify && window.Shopify.designMode) {
        this.addEventListener('shopify:block:select', this._onBlockSelect.bind(this));
        this.addEventListener('shopify:block:deselect', this._startPlayer.bind(this));
      }

      this._startPlayer();
    }

    disconnectedCallback() {
      this._pausePlayer();
    }

    get cycleSpeed() {
      return (parseInt(this.getAttribute('cycle-speed'), 10) || 0) * 1000;
    }

    async select(index, animate) {
      if (index < 0 || index === this.selectedIndex || this.hasPendingTransition) {
        return;
      }

      this.hasPendingTransition = true;

      var duration = animate === false ? 0 : FADE_DURATION;
      var outgoing = this.messages[this.selectedIndex];
      var incoming = this.messages[index];

      try {
        await this._animate(outgoing, { transform: ['translateY(0)', 'translateY(-8px)'], opacity: [1, 0] }, duration);
        outgoing.hidden = true;
        incoming.hidden = false;
        await this._animate(incoming, { transform: ['translateY(8px)', 'translateY(0)'], opacity: [0, 1] }, duration);
        this.selectedIndex = index;
      } finally {
        // Never leave this latched: a rotator stuck mid-transition would silently stop rotating for good.
        this.hasPendingTransition = false;
      }
    }

    _animate(element, keyframes, duration) {
      return new Promise(function (resolve) {
        var animation = element.animate(keyframes, { duration: duration, easing: FADE_EASING });

        animation.onfinish = resolve;
        animation.oncancel = resolve;

        // A Web Animation can be left unfinished — a backgrounded tab, a cancelled animation, a browser that never
        // fires the event. Settling on a timer as well keeps the rotation going regardless. Resolving twice is a no-op.
        setTimeout(resolve, duration + 50);
      });
    }

    _next() {
      // Hidden means this visitor is in the control bucket; there is nothing to animate.
      if (this.offsetParent === null) {
        return;
      }

      this.select((this.selectedIndex + 1) % this.messages.length);
    }

    _onBlockSelect(event) {
      this._pausePlayer();

      var selected = this.messages.find(function (message) {
        return message === event.target || message.contains(event.target);
      });

      this.select(this.messages.indexOf(selected), false);
    }

    _startPlayer() {
      this._pausePlayer();

      if (this.cycleSpeed > 0) {
        this._interval = setInterval(this._next.bind(this), this.cycleSpeed);
      }
    }

    _pausePlayer() {
      clearInterval(this._interval);
    }
  };

  if (!window.customElements.get('announcement-rotator')) {
    window.customElements.define('announcement-rotator', AnnouncementRotator);
  }
})();

/**
 * ---------------------------------------------------------------------------------------------------------------------
 * A/B TEST: UPDATED BUY BOX BEHAVIOUR
 * ---------------------------------------------------------------------------------------------------------------------
 *
 * Behaviours the Figma dev notes call for, plus the sticky bar trigger. All of them are scoped to the updated bucket so
 * the control keeps behaving exactly as it does today, and all of them read the bucket at the moment they run rather
 * than caching it, so they stay correct however late the A/B tool stamps the attribute.
 */
(function () {
  var ATTRIBUTE = 'st-1-ab-test';

  function isUpdatedBucket() {
    return document.documentElement.hasAttribute(ATTRIBUTE) || (document.body !== null && document.body.hasAttribute(ATTRIBUTE));
  }

  /**
   * Dev note: "on add, the cart page opens and the cart updates/refreshes".
   *
   * The theme already does exactly that for stores configured with a cart drawer: on a successful add it dispatches
   * cart:refresh with openMiniCart, which re-renders the mini cart section and slides it open. Rather than duplicating
   * that, the setting the theme reads is turned into a getter that reports "drawer" for this bucket only. The mini cart
   * section is already on the page (layout/theme.liquid renders it for every cart type except "page"), and the theme
   * suppresses its own add-to-cart toast when the drawer opens, so nothing else needs changing.
   *
   * The st-4 cart drawer redesign (see assets/custom.css and snippets/mini-cart-b.liquid) needs the same auto-open on
   * add, so its bucket forces "drawer" here too. It stays out of isUpdatedBucket() itself because that function also
   * gates unrelated st-1 behaviours (gallery dragging, variant preselect, sticky bar) that st-4 has no bearing on.
   */
  if (window.themeVariables && window.themeVariables.settings) {
    var settings = window.themeVariables.settings;
    var configuredCartType = settings.cartType;
    var DRAWER_ATTRIBUTE_ST4 = 'st-4-ab-test';

    function isDrawerBucket() {
      return isUpdatedBucket()
        || document.documentElement.hasAttribute(DRAWER_ATTRIBUTE_ST4)
        || (document.body !== null && document.body.hasAttribute(DRAWER_ATTRIBUTE_ST4));
    }

    try {
      Object.defineProperty(settings, 'cartType', {
        configurable: true,
        enumerable: true,
        get: function () {
          return isDrawerBucket() ? 'drawer' : configuredCartType;
        }
      });
    } catch (e) {
      // A non-configurable property would mean the theme changed; leaving the configured value is the safe fallback.
    }
  }

  /**
   * Dev note: "Second option is the default (selected)".
   *
   * Done by performing a real selection rather than by rendering the second card as pre-selected, so that the price,
   * the media gallery, the inventory notice and the ?variant= URL all move together through the theme's own variant
   * picker. Skipped when the URL already pins a variant, so a shared or reloaded link always wins.
   */
  document.addEventListener('DOMContentLoaded', function () {
    if (!isUpdatedBucket()) {
      return;
    }

    try {
      if (new URLSearchParams(window.location.search).has('variant')) {
        return;
      }
    } catch (e) {
      return;
    }

    var cards = document.querySelectorAll('.buy-box__variants-list > .variant-card');

    if (cards.length < 2 || cards[1].classList.contains('is-selected')) {
      return;
    }

    var input = cards[1].querySelector('.variant-card__input');

    if (!input || input.disabled) {
      return;
    }

    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /**
   * The rating is the shortest path to the reviews, so clicking it scrolls down to them. The reviews are rendered by
   * a review app (Loox) inside an "Apps" section, so the target is resolved at click time from a list of known
   * containers rather than pinned to a section id that changes whenever the page is rebuilt. With nothing to scroll
   * to, the click is a no-op rather than a jump to the top of the page.
   */
  document.addEventListener('click', function (event) {
    if (!event.target.closest('[data-scroll-to-reviews]')) {
      return;
    }

    var reviews = document.querySelector([
      '#looxReviews',
      '.loox-reviews-default',
      '.loox-reviews',
      '#shopify-product-reviews',
      '.jdgm-rev-widg',
      '.shopify-section--apps'
    ].join(', '));

    if (!reviews) {
      return;
    }

    event.preventDefault();
    reviews.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });

  /**
   * The sticky add to cart bar must only appear once the buy box has been scrolled past.
   *
   * The theme decides that by observing the form that contains #MainPaymentContainer — which in this bucket is the
   * hidden control buy box. A display:none element reports an all-zero rect, so the theme's test
   * (`rect.top + rect.height <= stickyHeaderOffset`) is satisfied on the very first frame and the bar appears at the
   * top of the page and never hides. So here the theme's observer is swapped for one watching the button that is
   * actually on screen: this buy box's own submit button.
   *
   * The rest of the logic deliberately mirrors the theme's: the bar appears once the button has passed under the
   * sticky header, and on small screens it steps aside for the footer.
   */
  function stickyHeaderOffset() {
    var styles = getComputedStyle(document.documentElement);

    var value = function (name) {
      return parseInt(styles.getPropertyValue(name), 10) || 0;
    };

    return value('--header-height') * value('--enable-sticky-header') +
      value('--announcement-bar-height') * value('--enable-sticky-announcement-bar');
  }

  function bindStickyForm() {
    var stickyForm = document.querySelector('product-sticky-form');

    if (!stickyForm) {
      return;
    }

    var trigger = document.querySelector('.buy-box__submit');

    // Nothing to re-point at: either the control is showing, or this product keeps the theme's buy box.
    if (!isUpdatedBucket() || !trigger) {
      return;
    }

    // The theme's observer is watching the hidden buy box, so its verdict is meaningless here.
    if (stickyForm.intersectionObserver) {
      stickyForm.intersectionObserver.disconnect();
    }

    if (stickyForm.updatedObserver) {
      stickyForm.updatedObserver.disconnect();
    }

    var footer = document.querySelector('.shopify-section--footer');
    var offset = stickyHeaderOffset();
    var isPassed = false;
    var isFooterVisible = false;

    stickyForm.hidden = true;

    stickyForm.updatedObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.target === footer) {
          isFooterVisible = entry.intersectionRatio > 0;
          return;
        }

        var rect = trigger.getBoundingClientRect();
        isPassed = entry.intersectionRatio === 0 && rect.top + rect.height <= offset;
      });

      if (window.matchMedia(window.themeVariables.breakpoints.pocket).matches) {
        stickyForm.hidden = !isPassed || isFooterVisible;
      } else {
        stickyForm.hidden = !isPassed;
      }
    }, { rootMargin: '-' + offset + 'px 0px 0px 0px' });

    stickyForm.updatedObserver.observe(trigger);

    if (footer) {
      stickyForm.updatedObserver.observe(footer);
    }
  }

  document.addEventListener('DOMContentLoaded', bindStickyForm);

  // Both the sticky bar and this buy box are re-rendered on every variant change, so the binding has to be re-made.
  // variant:changed fires before the theme swaps the markup in, hence the deferral to the next macrotask.
  document.addEventListener('variant:changed', function () {
    setTimeout(bindStickyForm, 0);
  });
})();

/**
 * ---------------------------------------------------------------------------------------------------------------------
 * A/B TEST: SECTION BEHAVIOUR IN THE UPDATED DESIGN
 * ---------------------------------------------------------------------------------------------------------------------
 *
 * Behaviour changes to shared sections, scoped to the updated bucket so the control keeps behaving exactly as it does
 * today and the comparison stays clean. The bucket is read at the moment each handler runs, so it stays correct however
 * late the A/B tool stamps the attribute.
 */
(function () {
  var ATTRIBUTE = 'st-1-ab-test';

  function isUpdatedBucket() {
    return document.documentElement.hasAttribute(ATTRIBUTE) || (document.body !== null && document.body.hasAttribute(ATTRIBUTE));
  }

  /**
   * GALLERY — swipeable on desktop as well as mobile.
   *
   * The gallery list is a native horizontal scroller (.hide-scrollbar is "overflow: auto hidden"), so touch devices can
   * already swipe it. On a mouse, though, dragging only works when the theme marks the scroller `draggable`, and it
   * only does that when the navigation arrows are turned off — so with arrows on there is no drag at all.
   *
   * Adding the attribute and running the theme's own draggability setup gives both: arrows and drag. Reusing the theme's
   * setup rather than reimplementing it also keeps the drag cursor, the momentum easing, and its guard that strips the
   * mouse listeners on touch devices so native scrolling is never fought.
   */
  function enableGalleryDragging() {
    if (!isUpdatedBucket()) {
      return;
    }

    document.querySelectorAll('.shopify-section--gallery scrollable-content').forEach(function (scroller) {
      // Already draggable means the theme set it up itself, and running its setup twice would duplicate the cursor.
      if (scroller.draggable || typeof scroller._setupDraggability !== 'function') {
        return;
      }

      scroller.setAttribute('draggable', '');
      scroller._setupDraggability();
      suspendSnapWhileDragging(scroller);
    });
  }

  /**
   * Keeps the drag smooth against the scroll snapping.
   *
   * The theme's drag sets scrollLeft on every mousemove. With "scroll-snap-type: x mandatory" the browser pulls each
   * of those assignments back to the nearest snap point, so the gallery resists the pointer rather than following it.
   * Suspending snapping for the duration of the drag fixes that, and restoring it once the movement has stopped lets
   * it settle on an item — smooth to drag, snapped on release.
   *
   * "Stopped" is detected from the scroll events themselves rather than a fixed delay, because the theme keeps
   * animating a momentum glide after the mouse is released and its length depends on how hard the drag was thrown.
   *
   * Only wired for pointers that can hover, mirroring the theme's own guard: touch scrolling is native, never assigns
   * scrollLeft, and so is not in conflict with snapping at all — which is why it already feels right.
   */
  function suspendSnapWhileDragging(scroller) {
    if (window.matchMedia('(hover: none)').matches) {
      return;
    }

    var STOPPED_DELAY = 120;

    // Movement below this is a click, not a drag, and must not reposition anything.
    var COMMIT_THRESHOLD = 5;

    var stoppedTimer = null;
    var isPointerDown = false;
    var isSettling = false;
    var startIndex = 0;
    var startOffset = 0;

    function galleryItems() {
      return Array.prototype.slice.call(scroller.querySelectorAll('.gallery__item'));
    }

    // Where CSS snapping measures from: "scroll-snap-align: start", offset by the scroll padding. Read from computed
    // style rather than hard-coded so it cannot drift out of step with the stylesheet.
    function snapGeometry() {
      var styles = getComputedStyle(scroller);
      var isRtl = styles.direction === 'rtl';
      var port = scroller.getBoundingClientRect();
      var padding = parseFloat(styles.getPropertyValue('scroll-padding-inline-start')) || 0;

      return { isRtl: isRtl, snapEdge: isRtl ? port.right - padding : port.left + padding };
    }

    function leadingEdgeOf(item, geometry) {
      var rect = item.getBoundingClientRect();

      return geometry.isRtl ? rect.right : rect.left;
    }

    // The item currently sitting closest to the snap edge — the one a viewer would call the current slide.
    function currentIndex() {
      var geometry = snapGeometry();
      var nearest = 0;
      var nearestDistance = Infinity;

      galleryItems().forEach(function (item, index) {
        var distance = Math.abs(leadingEdgeOf(item, geometry) - geometry.snapEdge);

        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearest = index;
        }
      });

      return nearest;
    }

    function offsetForIndex(index) {
      var items = galleryItems();

      if (items.length === 0) {
        return null;
      }

      var geometry = snapGeometry();
      var clampedIndex = Math.max(0, Math.min(items.length - 1, index));

      // In a right-to-left document scrollLeft runs from 0 down to -maxOffset.
      var maxOffset = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
      var lowest = geometry.isRtl ? -maxOffset : 0;
      var highest = geometry.isRtl ? 0 : maxOffset;

      // Moving a leading edge onto the snap edge shifts scrollLeft by the same signed amount in both directions.
      var target = scroller.scrollLeft + (leadingEdgeOf(items[clampedIndex], geometry) - geometry.snapEdge);

      return Math.max(lowest, Math.min(highest, target));
    }

    function release() {
      scroller.removeEventListener('scroll', onScroll);
      scroller.classList.remove('is-dragging');
      isSettling = false;
    }

    function onScrollStopped() {
      // Still settling means the glide below has finished, so snapping can be restored without moving anything.
      if (isSettling) {
        release();
        return;
      }

      var moved = scroller.scrollLeft - startOffset;

      // A click, or a nudge too small to mean anything. Leave the gallery exactly where it is.
      if (Math.abs(moved) < COMMIT_THRESHOLD) {
        release();
        return;
      }

      var geometry = snapGeometry();

      // Any deliberate drag commits to the next slide along, however short it was. Settling on whichever slide
      // happens to be nearest instead would send a drag of less than half a slide back where it came from, which
      // reads as the gallery refusing to move. A drag long enough to pass several slides still lands where it was
      // taken, because the nearest slide is then further along than the adjacent one.
      var forward = geometry.isRtl ? moved < 0 : moved > 0;
      var nearest = currentIndex();
      var index = forward ? Math.max(nearest, startIndex + 1) : Math.min(nearest, startIndex - 1);
      var target = offsetForIndex(index);

      if (target === null || Math.abs(target - scroller.scrollLeft) < 1) {
        release();
        return;
      }

      // Glide into place with snapping still suspended. Re-enabling scroll-snap does not animate — it repositions
      // instantly — so letting CSS do this is exactly what made the release jump a whole slide.
      isSettling = true;
      scroller.scrollTo({ left: target, behavior: 'smooth' });

      // The glide emits its own scroll events, which reschedule this; the timer only covers it emitting none.
      stoppedTimer = setTimeout(onScrollStopped, STOPPED_DELAY);
    }

    function onScroll() {
      clearTimeout(stoppedTimer);

      // Never settle mid-drag: while the button is held, the pointer is still in charge.
      if (!isPointerDown) {
        stoppedTimer = setTimeout(onScrollStopped, STOPPED_DELAY);
      }
    }

    scroller.addEventListener('mousedown', function () {
      clearTimeout(stoppedTimer);
      isPointerDown = true;
      isSettling = false;

      // Captured before the drag moves anything, so the settle knows which slide it started from and how far it came.
      startIndex = currentIndex();
      startOffset = scroller.scrollLeft;

      scroller.classList.add('is-dragging');
      scroller.addEventListener('scroll', onScroll);
    });

    // On document, because a drag very often ends with the pointer outside the gallery.
    document.addEventListener('mouseup', function () {
      if (!isPointerDown) {
        return;
      }

      isPointerDown = false;
      clearTimeout(stoppedTimer);

      // Waits out the theme's momentum glide first, whose length depends on how hard the drag was thrown.
      stoppedTimer = setTimeout(onScrollStopped, STOPPED_DELAY);
    });
  }

  document.addEventListener('DOMContentLoaded', enableGalleryDragging);

  /**
   * FAQ — one answer open at a time.
   *
   * <collapsible-content> announces itself with openable-element:open / :close, and the theme's toggle buttons listen
   * for those on document to keep their own aria-expanded in sync. So closing a sibling by setting `open = false` is
   * enough: the animation runs and the matching button follows, with nothing here having to touch the button state.
   *
   * Scoped to the wrapper the opened item belongs to, so a page with several FAQ sections keeps them independent.
   */
  document.addEventListener('openable-element:open', function (event) {
    if (!isUpdatedBucket()) {
      return;
    }

    var opened = event.target;

    if (!opened.matches || !opened.matches('.faq__item collapsible-content')) {
      return;
    }

    var wrapper = opened.closest('.faq__wrapper');

    if (!wrapper) {
      return;
    }

    wrapper.querySelectorAll('.faq__item collapsible-content[open]').forEach(function (other) {
      if (other !== opened) {
        other.open = false;
      }
    });
  });
})();

/**
 * ------------------------------------------------------------------------------------------------------------------
 * A/B TEST — CART DRAWER (VARIANT B)
 *
 * The theme re-renders the whole cart drawer by replacing its innerHTML on every cart change, and restores the scroll
 * position of the FIRST ".drawer__content" it finds. That element always belongs to the original drawer (variant A),
 * so the new drawer would jump back to the top after every quantity change. This restores its scroll position too.
 * ------------------------------------------------------------------------------------------------------------------
 */
(function () {
  var drawer = document.getElementById('mini-cart');

  if (!drawer) {
    return;
  }

  var savedScrollTop = null;

  // Captured on documentElement so that it runs before the theme swaps the markup out.
  document.documentElement.addEventListener('cart:refresh', function () {
    var body = drawer.querySelector('.cart-drawer__body');
    savedScrollTop = body ? body.scrollTop : null;
  }, true);

  new MutationObserver(function () {
    if (savedScrollTop === null) {
      return;
    }

    var body = drawer.querySelector('.cart-drawer__body');

    if (body) {
      body.scrollTop = savedScrollTop;
    }

    savedScrollTop = null;
  }).observe(drawer, { childList: true });
})();
